export const POOL_BLOCK_KEYS = {
    infoStatic: [
        'provider',
        'network',
        'poolAddress',
        'base',
        'quote',
        'pair',
        'initTimestamp',
        'initBlock',
        'initTxSignature',
        'accounts',
    ],
    metrics: [
        'priceNative',
        'priceUsd',
        'marketCapUsd',
        'liquidityUsd',
        'liquidityNative',
        'liquidityToken',
        'fees',
        'poolFee',
        'periodStats',
        'audit',
        'firstBuyers',
        'firstBuyersMeta',
        'holders',
        'insiders',
        'snipers',
        'bundlers',
        'initTxCreator',
        'allTimeHigh',
        'creatorStats',
    ],
    charts: ['priceChart'],
    premium: ['premiumInfo'],
    migration: ['migrated'],
};
// Fallback block set for producer messages that carry no explicit `blocks`.
// Producers with change detection (#2916) send the exact changed blocks
// (any of the five) themselves.
export const DEFAULT_PRODUCER_BLOCKS = [
    'infoStatic',
    'metrics',
    'charts',
];
export const POOL_BLOCK_FRESHNESS = {
    infoStatic: { source: (pool) => pool.infoUpdatedAt, compare: 'strict-gt' },
    metrics: { source: (pool) => pool.metricsUpdatedAt, compare: 'strict-gt' },
    charts: { source: (pool) => pool.chartsUpdatedAt, compare: 'strict-gt' },
    premium: {
        source: (pool) => pool.premiumInfo?.updatedAt,
        compare: 'strict-gt',
    },
    migration: {
        source: (pool) => pool.extraDetails?.migration?.migrationTimestamp,
        compare: 'always',
    },
};
const _assertCovered = true;
const _assertNoExtra = true;
void _assertCovered;
void _assertNoExtra;
const pickKeys = (pool, keys) => Object.fromEntries(keys.filter((k) => pool[k] !== undefined).map((k) => [k, pool[k]]));
export const resolveFreshness = (pool, spec) => spec.source(pool) ?? pool.updatedAt;
export const splitExtraDetails = (pool) => {
    const ed = pool.extraDetails;
    if (!ed) {
        return { infoStaticExtra: undefined, migration: undefined };
    }
    const { migration, ...rest } = ed;
    return {
        infoStaticExtra: rest,
        migration,
    };
};
const applyBlockSpecialCases = (block, pool, picked) => {
    if (block === 'infoStatic') {
        const { infoStaticExtra } = splitExtraDetails(pool);
        return infoStaticExtra
            ? { ...picked, extraDetails: infoStaticExtra }
            : picked;
    }
    if (block === 'migration') {
        const { migration } = splitExtraDetails(pool);
        return migration ? { ...picked, migration } : picked;
    }
    return picked; // metrics / charts / premium — plain Pick
};
export const buildBlock = (pool, block) => {
    const keys = POOL_BLOCK_KEYS[block];
    const updatedAt = resolveFreshness(pool, POOL_BLOCK_FRESHNESS[block]);
    const picked = pickKeys(pool, keys);
    const patched = applyBlockSpecialCases(block, pool, picked);
    return { updatedAt, ...patched };
};
const hasPremiumData = (premiumInfo) => Boolean(premiumInfo &&
    (premiumInfo.premium ||
        premiumInfo.boosters > 0 ||
        premiumInfo.status !== 'inactive'));
// Partial updates are PRODUCER-DRIVEN: the producer detects which blocks
// changed (chart-api-common pool-blocks classification, #2916) and requests
// exactly those; the consumer additionally pushes 'premium' via its redis
// cursor. Blocks are built verbatim — the only guard is skipping a premium
// block when the pool has no premiumInfo joined.
export const buildPartialUpdate = (pool, requested, source) => {
    const blocks = [...new Set(requested)].filter((b) => b !== 'premium' || Boolean(pool.premiumInfo));
    const built = Object.fromEntries(blocks.map((b) => [b, buildBlock(pool, b)]));
    return {
        kind: 'partial',
        protocolVersion: 1,
        address: pool.poolAddress,
        network: pool.network,
        source,
        ...built,
    };
};
// REST snapshot (additive /pair-info-blocks): unlike the producer-driven
// partial, a snapshot includes every block that carries data — premium only
// when it holds actual premium data (not the EMPTY_PREMIUM sentinel),
// migration only when the pool has migrated.
export const buildBlocksView = (pool) => {
    const blocks = [...DEFAULT_PRODUCER_BLOCKS];
    if (hasPremiumData(pool.premiumInfo)) {
        blocks.push('premium');
    }
    if (pool.extraDetails?.migration) {
        blocks.push('migration');
    }
    return Object.fromEntries(blocks.map((b) => [b, buildBlock(pool, b)]));
};
export const buildFullUpdate = (pool, source) => ({
    ...pool,
    infoUpdatedAt: resolveFreshness(pool, POOL_BLOCK_FRESHNESS.infoStatic),
    metricsUpdatedAt: resolveFreshness(pool, POOL_BLOCK_FRESHNESS.metrics),
    chartsUpdatedAt: resolveFreshness(pool, POOL_BLOCK_FRESHNESS.charts),
    kind: 'full',
    source,
    protocolVersion: 1,
});
//# sourceMappingURL=pool-blocks.js.map